// src/components/ui/MagicalElementsForm.jsx
import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  FormControl,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
  useTheme,
  alpha,
  CardContent,
  Avatar,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon,
  Star as StarIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoFixHigh as WandIcon,
  Lightbulb as IdeaIcon,
  QuestionMark as QuestionIcon,
  DesignServices as DesignIcon,
  Animation as AnimationIcon,
  Palette as PaletteIcon,
  Flare as FlareIcon,
  Favorite as HeartIcon,
} from '@mui/icons-material';

// Import custom components
import { Card, FormSection } from '../../design-system';

// Enchantment level options with enhanced descriptions
const ENCHANTMENT_LEVELS = [
  { 
    value: 'subtle', 
    label: 'Subtle',
    icon: <FlareIcon fontSize="small" />,
    description: 'Light touches of magic with elegant animations and gentle transitions',
    examples: ['Subtle hover effects', 'Soft color transitions', 'Light animations']
  },
  { 
    value: 'moderate', 
    label: 'Moderate',
    icon: <WandIcon fontSize="small" />,
    description: 'More noticeable magical elements with storytelling and surprising interactions',
    examples: ['Animated illustrations', 'Particle effects', 'Interactive elements']
  },
  { 
    value: 'prominent', 
    label: 'Prominent',
    icon: <MagicIcon fontSize="small" />,
    description: 'Bold magical elements that create immersive, fantastical experiences',
    examples: ['Full page animations', 'Visual stories', 'Surprise & delight moments']
  },
];

// Special feature categories with suggestions
const SPECIAL_FEATURE_CATEGORIES = [
  {
    name: 'Visual Effects',
    icon: <PaletteIcon />,
    suggestions: [
      'Color Transitions',
      'Particle Effects',
      'Soft Glows',
      'Custom Illustrations',
      'Visual Metaphors',
    ]
  },
  {
    name: 'Animation',
    icon: <AnimationIcon />,
    suggestions: [
      'Subtle Animation',
      'Micro-interactions',
      'Scroll Animations',
      'Page Transitions',
      'Loading Animations',
    ]
  },
  {
    name: 'Interactivity',
    icon: <DesignIcon />,
    suggestions: [
      'Interactive Elements',
      'Click Surprises',
      'Hover Transformations',
      'Unexpected Reactions',
      'Hidden Features',
    ]
  },
  {
    name: 'Personalization',
    icon: <HeartIcon />,
    suggestions: [
      'Personalization',
      'Custom Greetings',
      'Adaptive Content',
      'User Name Integration',
      'Preference-driven Experiences',
    ]
  },
];

// All suggestions flattened for autocomplete
const SPECIAL_FEATURE_SUGGESTIONS = SPECIAL_FEATURE_CATEGORIES.flatMap(category => 
  category.suggestions
);

/**
 * Enhanced MagicalElementsForm component with better visual design
 * Compatible with both entity data and direct data patterns
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Current entity/form data
 * @param {Function} props.onChange - Standard onChange handler
 * @param {Function} props.onNestedChange - Nested field change handler
 * @param {Object} props.errors - Validation errors
 * @param {Object} props.touched - Fields touched state
 * @param {string} props.entityType - Type of entity (brand, campaign, client, etc.)
 * @returns {React.ReactElement}
 */
const MagicalElementsForm = ({ 
  data, 
  onChange, 
  onNestedChange,
  errors = {},
  touched = {},
  entityType = 'content'
}) => {
  const theme = useTheme();
  const [newFeature, setNewFeature] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  
  // Get magical elements or initialize empty
  const magicalElements = data.magicalElements || {
    transformationNarrative: '',
    enchantmentLevel: 'moderate',
    specialFeatures: []
  };
  
  // Handle enchantment level change
  const handleEnchantmentLevelChange = (e) => {
    const value = e.target.value;
    // Support different change handler patterns
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // name, value pattern
        onChange('magicalElements.enchantmentLevel', value);
      } else {
        // event pattern
        onChange(e);
      }
    }
    if (onNestedChange && typeof onNestedChange === 'function') {
      onNestedChange('magicalElements', 'enchantmentLevel', value);
    }
  };
  
  // Handle transformation narrative change
  const handleNarrativeChange = (e) => {
    const value = e.target.value;
    // Support different change handler patterns
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // name, value pattern
        onChange('magicalElements.transformationNarrative', value);
      } else {
        // event pattern
        onChange(e);
      }
    }
    if (onNestedChange && typeof onNestedChange === 'function') {
      onNestedChange('magicalElements', 'transformationNarrative', value);
    }
  };
  
  // Handle adding a special feature
  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    
    const updatedFeatures = [...(magicalElements.specialFeatures || []), newFeature.trim()];
    
    // Create a synthetic event object for event-based onChange
    const syntheticEvent = { 
      target: {
        name: 'magicalElements.specialFeatures',
        value: updatedFeatures
      }
    };
    
    // Support different change handler patterns
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // name, value pattern
        onChange('magicalElements.specialFeatures', updatedFeatures);
      } else {
        // event pattern
        onChange(syntheticEvent);
      }
    }
    if (onNestedChange && typeof onNestedChange === 'function') {
      onNestedChange('magicalElements', 'specialFeatures', updatedFeatures);
    }
    setNewFeature('');
  };
  
  // Handle removing a special feature
  const handleRemoveFeature = (index) => {
    const updatedFeatures = [...magicalElements.specialFeatures];
    updatedFeatures.splice(index, 1);
    
    // Create a synthetic event object for event-based onChange
    const syntheticEvent = { 
      target: {
        name: 'magicalElements.specialFeatures',
        value: updatedFeatures
      }
    };
    
    // Support different change handler patterns
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // name, value pattern
        onChange('magicalElements.specialFeatures', updatedFeatures);
      } else {
        // event pattern
        onChange(syntheticEvent);
      }
    }
    if (onNestedChange && typeof onNestedChange === 'function') {
      onNestedChange('magicalElements', 'specialFeatures', updatedFeatures);
    }
  };
  
  // Handle adding a suggestion directly
  const handleAddSuggestion = (suggestion) => {
    if (magicalElements.specialFeatures?.includes(suggestion)) return;
    
    const updatedFeatures = [...(magicalElements.specialFeatures || []), suggestion];
    
    // Create a synthetic event object for event-based onChange
    const syntheticEvent = { 
      target: {
        name: 'magicalElements.specialFeatures',
        value: updatedFeatures
      }
    };
    
    // Support different change handler patterns
    if (onChange && typeof onChange === 'function') {
      if (onChange.length === 2) {
        // name, value pattern
        onChange('magicalElements.specialFeatures', updatedFeatures);
      } else {
        // event pattern
        onChange(syntheticEvent);
      }
    }
    if (onNestedChange && typeof onNestedChange === 'function') {
      onNestedChange('magicalElements', 'specialFeatures', updatedFeatures);
    }
  };
  
  // Get level icons based on enchantment level
  const getLevelIcons = (level) => {
    const iconColor = theme.palette.accent?.main || theme.palette.secondary.main || '#FFD700';
    
    switch(level) {
      case 'subtle':
        return (
          <StarIcon 
            fontSize="small" 
            sx={{ color: alpha(iconColor, 0.5), mr: 1 }} 
          />
        );
      case 'moderate':
        return (
          <>
            <StarIcon fontSize="small" sx={{ color: alpha(iconColor, 0.7), mr: 0.25 }} />
            <StarIcon fontSize="small" sx={{ color: alpha(iconColor, 0.7), mr: 1 }} />
          </>
        );
      case 'prominent':
        return (
          <>
            <StarIcon fontSize="small" sx={{ color: iconColor, mr: 0.25 }} />
            <StarIcon fontSize="small" sx={{ color: iconColor, mr: 0.25 }} />
            <StarIcon fontSize="small" sx={{ color: iconColor, mr: 1 }} />
          </>
        );
      default:
        return null;
    }
  };
  
  // Find current enchantment level details
  const currentLevelDetails = ENCHANTMENT_LEVELS.find(
    level => level.value === magicalElements.enchantmentLevel
  ) || ENCHANTMENT_LEVELS[1]; // Default to moderate
  
  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <FormSection
          icon={<MagicIcon />}
          title="Magical Elements"
          description={`Enhance your ${entityType} with magical elements that create a memorable experience for your audience.`}
        />
      </Grid>
      
      {/* Enchantment Level */}
      <Grid item xs={12}>
        <Card magical={true} level={2}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mr: 2
                }}
              >
                <MagicIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Enchantment Level
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Select how prominently magical elements should appear
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={3}>
              {ENCHANTMENT_LEVELS.map((level) => (
                <Grid item xs={12} md={4} key={level.value}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      border: level.value === magicalElements.enchantmentLevel 
                        ? `2px solid ${theme.palette.primary.main}` 
                        : `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      },
                      position: 'relative',
                      overflow: 'visible'
                    }}
                    onClick={() => {
                      // Create a synthetic event object for event-based onChange
                      const syntheticEvent = { 
                        target: {
                          name: 'magicalElements.enchantmentLevel',
                          value: level.value
                        }
                      };
                      
                      // Support different change handler patterns
                      if (onChange && typeof onChange === 'function') {
                        if (onChange.length === 2) {
                          // name, value pattern
                          onChange('magicalElements.enchantmentLevel', level.value);
                        } else {
                          // event pattern
                          onChange(syntheticEvent);
                        }
                      }
                      if (onNestedChange && typeof onNestedChange === 'function') {
                        onNestedChange('magicalElements', 'enchantmentLevel', level.value);
                      }
                    }}
                  >
                    {level.value === magicalElements.enchantmentLevel && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2,
                          zIndex: 1
                        }}
                      >
                        <WandIcon fontSize="small" />
                      </Box>
                    )}
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box 
                          sx={{ 
                            color: level.value === magicalElements.enchantmentLevel 
                              ? theme.palette.primary.main 
                              : theme.palette.text.secondary,
                            display: 'flex',
                            alignItems: 'center',
                            width: 80,
                            justifyContent: 'flex-start'
                          }}>
                          {level.value === 'subtle' && <FlareIcon sx={{ mr: 1 }} />}
                          {level.value === 'moderate' && <WandIcon sx={{ mr: 1 }} />}
                          {level.value === 'prominent' && <MagicIcon sx={{ mr: 1 }} />}
                          <Typography 
                            variant="subtitle1"
                            sx={{
                              fontWeight: level.value === magicalElements.enchantmentLevel 
                                ? 600 
                                : 400
                            }}
                          >
                            {level.label}
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            ml: 'auto',
                            opacity: level.value === magicalElements.enchantmentLevel ? 1 : 0.7
                          }}
                        >
                          {level.value === 'subtle' && (
                            <StarIcon fontSize="small" sx={{ color: alpha(theme.palette.warning.main, 0.6) }} />
                          )}
                          {level.value === 'moderate' && (
                            <>
                              <StarIcon fontSize="small" sx={{ color: alpha(theme.palette.warning.main, 0.8) }} />
                              <StarIcon fontSize="small" sx={{ color: alpha(theme.palette.warning.main, 0.8) }} />
                            </>
                          )}
                          {level.value === 'prominent' && (
                            <>
                              <StarIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              <StarIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              <StarIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                            </>
                          )}
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color={level.value === magicalElements.enchantmentLevel 
                          ? 'textPrimary' 
                          : 'textSecondary'}
                        paragraph
                        sx={{ 
                          opacity: level.value === magicalElements.enchantmentLevel ? 1 : 0.8,
                          minHeight: 60
                        }}
                      >
                        {level.description}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 0.5,
                          mt: 1,
                          opacity: level.value === magicalElements.enchantmentLevel ? 1 : 0.7
                        }}
                      >
                        {level.examples.map((example, idx) => (
                          <Chip 
                            key={idx} 
                            label={example} 
                            size="small" 
                            variant="outlined"
                            color={level.value === magicalElements.enchantmentLevel ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
      </Grid>
      
      {/* Transformation Narrative */}
      <Grid item xs={12}>
        <Card magical={true} level={1}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  mr: 2
                }}
              >
                <WandIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Transformation Narrative
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Describe how your {entityType} transforms the audience's experience
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.background.default, 0.6), borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <IdeaIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.warning.main }} />
                This narrative helps content creators understand the magical experience your {entityType} delivers
                to your audience. Be specific about the transformation you want to create.
              </Typography>
            </Box>
            
            <TextField
              multiline
              rows={6}
              fullWidth
              name="magicalElements.transformationNarrative"
              value={magicalElements.transformationNarrative || ''}
              onChange={handleNarrativeChange}
              placeholder={`e.g., Our ${entityType} transforms ordinary moments into extraordinary experiences by...`}
              helperText="This shapes how magical elements are incorporated into your content"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused': {
                    '& fieldset': {
                      borderColor: theme.palette.secondary.main,
                      borderWidth: 2
                    }
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.secondary.light
                  }
                }
              }}
            />
            
            <Box 
              sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: alpha(theme.palette.secondary.main, 0.05), 
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.palette.secondary.main, 0.3)}`,
                display: 'flex'
              }}
            >
              <Box sx={{ mr: 2, color: theme.palette.secondary.main }}>
                <WandIcon />
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Writing Tips:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Use sensory language to describe the experience
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Focus on the emotional impact you want to create
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Consider how it aligns with your {entityType}'s core values
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Describe the before and after states of the transformation
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Grid>
      
      {/* Special Features */}
      <Grid item xs={12}>
        <Card magical={true} level={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mr: 2
                }}
              >
                <StarIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Special Magical Features
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add specific magical elements to incorporate into your {entityType}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Current features */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Features:
              </Typography>
              
              {magicalElements.specialFeatures && magicalElements.specialFeatures.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {magicalElements.specialFeatures.map((feature, index) => (
                    <Chip
                      key={index}
                      label={feature}
                      onDelete={() => handleRemoveFeature(index)}
                      color="primary"
                      icon={<StarIcon />}
                      variant="outlined"
                      sx={{ borderWidth: 1.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.background.default, 0.6), 
                    borderRadius: 1,
                    border: `1px dashed ${theme.palette.divider}`,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No special features added yet. Add features from the categories below.
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Add new feature */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Autocomplete
                freeSolo
                options={SPECIAL_FEATURE_SUGGESTIONS}
                value={newFeature}
                onInputChange={(e, value) => setNewFeature(value)}
                sx={{ flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Special Feature"
                    placeholder="e.g., Subtle Animation"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused': {
                          '& fieldset': {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 2
                          }
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.light
                        }
                      }
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddFeature}
                disabled={!newFeature.trim()}
                sx={{ py: 1 }}
              >
                Add
              </Button>
            </Box>
            
            {/* Feature Categories */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Feature Categories:
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {SPECIAL_FEATURE_CATEGORIES.map((category, index) => (
                  <Chip
                    key={index}
                    label={category.name}
                    icon={category.icon}
                    onClick={() => setActiveCategory(activeCategory === index ? null : index)}
                    color={activeCategory === index ? 'primary' : 'default'}
                    variant={activeCategory === index ? 'filled' : 'outlined'}
                    sx={{ height: 36 }}
                  />
                ))}
              </Box>
              
              {activeCategory !== null && (
                <Fade in={activeCategory !== null}>
                  <Card 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                    }}
                  >
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      {SPECIAL_FEATURE_CATEGORIES[activeCategory].name} Suggestions:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {SPECIAL_FEATURE_CATEGORIES[activeCategory].suggestions.map((suggestion, index) => {
                        const isAlreadyAdded = magicalElements.specialFeatures?.includes(suggestion);
                        return (
                          <Chip
                            key={index}
                            label={suggestion}
                            onClick={() => !isAlreadyAdded && handleAddSuggestion(suggestion)}
                            color={isAlreadyAdded ? 'success' : 'primary'}
                            variant="outlined"
                            icon={isAlreadyAdded ? <WandIcon /> : null}
                            disabled={isAlreadyAdded}
                            sx={{ 
                              borderStyle: isAlreadyAdded ? 'dashed' : 'solid',
                              opacity: isAlreadyAdded ? 0.7 : 1
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Card>
                </Fade>
              )}
              
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: alpha(theme.palette.background.default, 0.6), 
                  borderRadius: 1,
                  border: `1px dashed ${theme.palette.divider}`,
                  display: 'flex'
                }}
              >
                <Box sx={{ color: 'info.main', mr: 2 }}>
                  <IdeaIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Why add magical features?
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Magical elements create memorable experiences that help your {entityType} stand out from competitors.
                    They add unexpected delight to user interactions and can increase engagement by making your
                    content more emotionally resonant.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default MagicalElementsForm;